import {Box, Text} from 'exsportia-components'

import {GetBookingCancelDetailsReturnType} from '../../../api/booking/get-booking-cancel-details-data'
import {FULL_CANCEL_POLICY_OPTIONS_MAP} from '../../../constants/cancel-policy'
import {CurrenciesEnum} from '../../../types/currencies'
import {DividerWithContent} from '../../../ui/components/divider-with-content/divider-with-content'
import {GradientLoader} from '../../../ui/components/gradient-loader/gradient-loader'
import {LabeledValueRow} from '../../../ui/components/key-value-row/labeled-value-row'
import {LabeledSection} from '../../../ui/components/labeled-section/labeled-section'
import {useLocale} from '../../../utils/i18n'
import {formatRainCheckMoney, getRainCheckCancelView} from '../rain-check/rain-check-content'

export const CancelInfoComponent = ({
  isLoading,
  cancelDetails,
  currency,
}: {
  isLoading: boolean
  currency: CurrenciesEnum
  cancelDetails?: GetBookingCancelDetailsReturnType
}) => {
  const {l} = useLocale()
  const {
    fitInCancelPolicy,
    isAbleToCancel,
    totalRefundedAmount,
    cancelPolicy = '',
    fee,
    availableAmountForRefund,
    bookingWithSubscription,
    totalPaidAmount,
    subUsageWillBeRefunded,
    venueCancelPolicy,
    venueCancelQuote,
  } = cancelDetails || {}

  if (isLoading) {
    return (
      <Box>
        <GradientLoader height={'50px'}/>
        <DividerWithContent />
        <GradientLoader height={'50px'}/>
        <DividerWithContent />
        <GradientLoader height={'50px'}/>
        <DividerWithContent />
        <GradientLoader height={'25px'}/>
      </Box>
    )
  }
  if (!isAbleToCancel) {
    return null
  }

  const rain = getRainCheckCancelView(cancelDetails)
  if (rain) {
    const money = (value: number) => formatRainCheckMoney(l, value, currency)
    return (
      <Box data-testid='rain-check-cancel-info'>
        <LabeledSection label={l('rain_check_title')}>
          {rain.reason && <Text text={rain.reason} />}
          {rain.policyText && <Text text={rain.policyText} />}
        </LabeledSection>
        <DividerWithContent />
        {rain.refundable > 0 ? (
          <>
            <LabeledValueRow label={l('rain_check_modal_get_back')}>{money(rain.refundable)}</LabeledValueRow>
            {rain.notReturned !== null && (
              <LabeledValueRow label={l('rain_check_modal_not_returned')}>{money(rain.notReturned)}</LabeledValueRow>
            )}
            {rain.shareReason && <Text text={rain.shareReason} />}
          </>
        ) : (
          <Text text={l(rain.paidSomething ? 'rain_check_free_text_paid' : 'rain_check_free_text')} />
        )}
      </Box>
    )
  }

  return (
    <Box>
      <LabeledSection label={l('cancelPolicy')}>
        {venueCancelPolicy ? (
          <>
            <Text text={venueCancelPolicy.name} />
            {venueCancelPolicy.customerSummary && <Text text={venueCancelPolicy.customerSummary} />}
          </>
        ) : (
          <Text
            text={
              // eslint-disable-next-line
              FULL_CANCEL_POLICY_OPTIONS_MAP[cancelPolicy]
                ? l(FULL_CANCEL_POLICY_OPTIONS_MAP[cancelPolicy].description)
                : cancelPolicy
            }
          />
        )}
      </LabeledSection>
      {venueCancelQuote && Number.isFinite(Number(venueCancelQuote.refundable)) && (
        <>
          <DividerWithContent />
          <LabeledValueRow label={l('venueCancelPolicy_youGetBack', {value: Number(venueCancelQuote.refundPercent ?? 0)})}>
            {l(`${currency}_symbolic`, {value: Number(venueCancelQuote.refundable)})}
          </LabeledValueRow>
        </>
      )}
      <DividerWithContent />
      <LabeledValueRow label={l('totalPaid')}>{l(`${currency}_symbolic`, {value: totalPaidAmount})}</LabeledValueRow>
      <LabeledValueRow label={l('refunded')}>{l(`${currency}_symbolic`, {value: totalRefundedAmount})}</LabeledValueRow>
      <DividerWithContent />
      <LabeledValueRow label={'Fit in cancel policy'}>{l(fitInCancelPolicy ? 'yes' : 'no', {caseAction: 'upperCase'})}</LabeledValueRow>
      <LabeledValueRow label={l('availableForRefund')}>{l(`${currency}_symbolic`, {value: availableAmountForRefund})}</LabeledValueRow>
      <LabeledValueRow label={l('fee')}>{l(`${currency}_symbolic`, {value: fee})}</LabeledValueRow>
      {bookingWithSubscription && (
        <>
          <DividerWithContent />
          <LabeledValueRow label={l('refundSubscriptionUsage')}>
            {l(subUsageWillBeRefunded ? 'yes' : 'no', {caseAction: 'upperCase'})}
          </LabeledValueRow>
        </>
      )}
    </Box>
  )
}


